import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { useIsPro } from 'hooks/useIsPro';
import { usePersistedState } from 'hooks/usePersistedState';
import cn from 'classnames';

import beaconLogo from 'assets/BeaconLogo.svg';
import beaconLogoFull from 'assets/BeaconLogoFull.svg';
import homeButton from 'assets/HomeButtonBlack.svg';
import sidebarCollapse from 'assets/sidebar-collapse.svg';
import defaultUserAvatar from 'assets/user_avatar.svg';

import { ROUTES } from 'routes/route.constants';

import SidebarMenu from './SidebarMenu';

const Sidebar = () => {
  const [isCollapsed, setIsCollapsed] = usePersistedState('sidebar-collapsed', true);
  const currentLocation = useLocation();

  const authUser = useSelector(state => state.userStore);
  const isPro = useIsPro();

  const isOnDashboard = currentLocation.pathname === ROUTES.TEAM_HOME;

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const [menuOpen, setMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  const getBrandLogo = () => {
    // Return customer's custom logos if available, otherwise return default logos

    if (
      authUser?.customer?.customer_logos
      && Object.keys(authUser.customer.customer_logos).length > 0
    ) {
      return isCollapsed
        ? authUser.customer.customer_logos.icon
        : authUser.customer.customer_logos.full;
    }

    return isCollapsed ? beaconLogo : beaconLogoFull;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const onClickOutside = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', onClickOutside);

    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  return (
    <div
      className={cn(
        'bg-gray-50 transition-all duration-300 min-h-screen top-0 flex flex-col shrink-0',
        {
          'w-[72px]': isCollapsed,
          'w-[208px]': !isCollapsed,
        }
      )}
    >
      <div className="flex-1">
        <div
          className="flex items-center px-6 py-5 mt-8 flex items-center justify-between
          border-b border-b-['#E1E4E8']"
        >
          {!isCollapsed && (
            <img
              src={getBrandLogo()}
              alt="Beacon Logo"
              className={cn(
                'transition-opacity duration-300 opacity-100',
                {
                  'w-6': isCollapsed,
                }
              )}
            />
          )}
          <img src={sidebarCollapse} alt="collapse" className="ml-[2px] pointer mt-4" onClick={toggleSidebar} />
        </div>

        <Link
          to={ROUTES.TEAM_HOME}
          className={cn(
            'flex items-center gap-12 px-16 py-2 rounded-md mx-12 my-16 cursor-pointer border-none no-underline',
            {
              'bg-gray-100': isOnDashboard,
              'bg-transparent hover:bg-gray-50': !isOnDashboard,
            }
          )}
        >
          <img src={homeButton} alt="Home" className="w-5 h-5" />
          {!isCollapsed && (
            <span className="text-sm font-medium text-gray-900">
              Dashboard
            </span>
          )}
        </Link>
      </div>

      <div className="p-4 border-t border-t-['#E1E4E8']">
        {/* Avatar + dropdown */}
        <div ref={dropdownRef}>
          <button
            onClick={() => setMenuOpen(dashboardNavbarMenuOpen => !dashboardNavbarMenuOpen)}
            className="flex items-center gap-16 py-1 focus:outline-none hover:border-gray-300"
          >

            {/* Avatar */}
            <div className="overflow-hidden flex gap-10 items-center justify-center">
              <img
                src={authUser?.profile_photo || defaultUserAvatar}
                alt="User Avatar"
                className="h-8 w-8 rounded-full"
              />
              {!isCollapsed && (
                <div className="flex flex-column gap-4">
                  <p
                    className="text-[#121723] font-medium text-base leading-5"
                  >
                    {authUser.full_name}
                  </p>
                  <p className="text-xs text-[#606776] text-left">
                    {isPro ? 'Pro tier' : 'Free tier'}
                  </p>
                </div>
              )}
            </div>
          </button>

          {menuOpen && (
            <SidebarMenu />
          )}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
